import { Session, SessionData } from '@ally/federated-types'
import { flow } from '@ally/utilitarian'
import { withAppHeaders, withContentType } from '@ally/fido'
import { HostData } from '@ally/data-types-host'
import dayjs from 'dayjs'
import logRocket from 'logrocket'

interface loadDataProps {
  accountId: string
  session: Session
  hostData: HostData
  isAfgStkEnabled: boolean
}

export interface AccountError {
  errorCode: string
  errorDescription: string
}

interface fetchDataProps<T> extends loadDataProps {
  type: T
}

export interface Bucket {
  accountBucketId: string
  bucketBalance: number
  bucketCategoryId: number
  bucketCategoryName: string
  bucketCreationDate: string
  bucketGoalAmount: number
  bucketGoalTargetDate: string
  bucketNickName: string
  isCoreSavings: number
  monetaryGoalProgress: number
  updatedByUser: string
  displayName?: string
}

export interface BoosterAccount {
  accountNickname: string
  accountNumber: string
  accountType: string
  availableBalancePvtEncrypt: number
  boosterAccountCategory: string
  externalGuid: string
  institutionName: string
  stkAccountId: string
}

export interface BoosterBucket {
  allocationAmount: number
  bucketCategoryId: number
  bucketCategoryName: string
  bucketId: string
  isCoreSavings: number
}

export type Capabilities =
  | 'VIEW_BOOSTER'
  | 'EDIT_BOOSTER'
  | 'DELETE_BOOSTER'
  | 'VIEW_BOOSTER_TRANSACTIONS'
  | 'VIEW_MONITORED_TRANSACTIONS'
  | 'VIEW_ACCOUNT_DETAILS'

export interface Booster {
  accountBoosterId: string
  accounts: BoosterAccount[]
  boosterCreationDate: string
  boosterStatus: string
  boosterType: string
  buckets: BoosterBucket[]
  capabilities: Capabilities[]
  cumulativeBalance: number
  depositAmount: number
  frequency: number
  isEditable: string
  nextPaymentDueDate: string
  updatedByUser: string
}

export interface DailyBalanceObj {
  accountBalanceOnDate: number
  date: string
}

export interface DepositObj {
  aggregateDeposit: number
  depositType: string
}

export interface MonthlyDepositSummaryObj {
  date: string
  deposits: DepositObj[]
}

export type MonthlyDataSet = [string, number]

export interface MonthlyDataSummary {
  monthFull: string
  monthAbbreviated: string
  aggregateTotal: number
  dataSets: MonthlyDataSet[]
}

export interface GrowthChart {
  dailyBalance: DailyBalanceObj[]
  monthlyDepositSummary: MonthlyDepositSummaryObj[]
  monthToDateSummary?: MonthlyDataSummary
}

enum DataTypes {
  buckets = 'buckets',
  boosters = 'boosters',
  growthChart = 'growthChart',
}

export interface StkDataObj {
  buckets: Bucket[]
  boosters: Booster[]
  growthChart: GrowthChart
  hasGrowth: boolean
}

const fetchStkData = async <T extends DataTypes>({
  accountId,
  type,
  session,
  isAfgStkEnabled,
}: fetchDataProps<T>): Promise<
  T extends DataTypes.buckets
    ? Pick<StkDataObj, 'buckets'>
    : T extends DataTypes.boosters
    ? Pick<StkDataObj, 'boosters'>
    : Pick<StkDataObj, 'growthChart'>
> => {
  const { data } = session
  const {
    AwsAccessToken,
    CSRFChallengeToken,
    access_token: accessToken,
  } = data as SessionData
  const endpoint = type === 'growthChart' ? 'charts/growth' : type
  const endpointType = type === 'growthChart' ? 'chartdata' : endpoint

  if (isAfgStkEnabled) {
    const endpointUrl = `/acs/v1/bank-accounts/${endpointType}/accounts/${accountId}/${endpoint}`

    const response = await fetch(
      endpointUrl,
      flow(
        withAppHeaders,
        withContentType,
      )({
        headers: {
          // /sts token for transmit v2 API's
          Authorization: `Bearer ${accessToken}`,
          accountId,
          guid: data?.guid,
          cif: data?.cif,
        },
      }),
    )

    if (!response.ok) {
      const unexpectedError = `STK ${endpointType} fetch - Unexpected Error`
      logRocket.captureMessage(`ally-next-remote-dashboard: ${unexpectedError}`)
      throw new Error(unexpectedError)
    }

    return response.json()
  }

  const endpointUrl = `/capi-gw/accounts/${accountId}/${endpoint}`
  const awsToken =
    AwsAccessToken ||
    (sessionStorage &&
      JSON.parse(sessionStorage.getItem('AwsAccessToken') || ''))

  return fetch(endpointUrl, {
    method: 'GET',
    headers: {
      ...(CSRFChallengeToken ? { CSRFChallengeToken } : {}),
      Authorization: `Bearer ${awsToken}`,
      'patron-id': 'olbWeb',
      'X-Requested-With': 'XMLHttpRequest',
      Accept: 'application/json, text/javascript, */*; q=0.01',
    } as HeadersInit,
  }).then(response => response.json())
}

type RawDataArray = [
  { buckets: Bucket[] },
  { boosters: Booster[] },
  { growthChart: GrowthChart },
]

function getFormattedStkData(rawData: RawDataArray): StkDataObj {
  const [{ buckets }, boostersObject, { growthChart }] = rawData
  if (!Array.isArray(buckets) || !Array.isArray(boostersObject.boosters)) {
    throw new Error('Buckets or Boosters are not an array')
  }
  const formattedBuckets = buckets.map((bucket: Bucket) => {
    return {
      ...bucket,
      displayName: bucket.bucketNickName || bucket.bucketCategoryName,
    }
  })
  const lastMonthIndex = growthChart.monthlyDepositSummary.length - 1
  const currentMonth = growthChart.monthlyDepositSummary[lastMonthIndex] || {
    date: '',
    deposits: [],
  }
  const monthFull = dayjs(currentMonth.date).format('MMMM')
  const monthAbbreviated = dayjs(currentMonth.date).format('MMM')
  const aggregateTotal = currentMonth.deposits.reduce(
    (a: number, b: DepositObj) => a + b.aggregateDeposit,
    0,
  )
  const dataSets: MonthlyDataSet[] = currentMonth.deposits.map(
    (deposit: DepositObj) => {
      const { aggregateDeposit, depositType } = deposit
      return [depositType, aggregateDeposit]
    },
    [],
  )

  const monthToDateSummary = {
    monthFull,
    monthAbbreviated,
    dataSets,
    aggregateTotal,
  }

  const hasGrowth =
    monthToDateSummary.aggregateTotal > 0 &&
    monthToDateSummary.dataSets.length > 0

  return {
    buckets: [...formattedBuckets],
    ...boostersObject,
    growthChart: {
      monthToDateSummary,
      ...growthChart,
    },
    hasGrowth,
  }
}

export const loadStkData = ({
  accountId,
  session,
  hostData,
  isAfgStkEnabled,
}: loadDataProps): Promise<StkDataObj> => {
  const pendingStkData = [
    DataTypes.buckets,
    DataTypes.boosters,
    DataTypes.growthChart,
  ].map(type =>
    fetchStkData({
      type,
      accountId,
      session,
      isAfgStkEnabled,
      hostData,
    }),
  )
  return Promise.all(pendingStkData).then(rawData =>
    getFormattedStkData(rawData as RawDataArray),
  )
}
